import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  CircularProgress,
  Alert,
  Typography,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Chip,
  Divider,
  Stack,
  Avatar
} from '@mui/material';
import { Edit as EditIcon, CloudUpload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api, { updateUserRoles, getPaginatedUsers, bulkAssignTherapistsToSupervisors, removeTherapistFromSupervisor } from '../../../services/api';

function EditTherapistModal({ open, therapist, onClose, onSuccess, token }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [maxClientCapacity, setMaxClientCapacity] = useState(20);
  const [canReceiveAutoAssignments, setCanReceiveAutoAssignments] = useState(true);
  const [availableSupervisors, setAvailableSupervisors] = useState([]);
  const [selectedSupervisors, setSelectedSupervisors] = useState([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);

  // Client-facing profile. These render on the invitation landing page and the
  // intake slot picker, so they are curated centrally rather than left to each
  // therapist. Backed by /api/admin/therapist-profile/:userId.
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Fetch available supervisors
  useEffect(() => {
    if (open) {
      loadSupervisors();
    }
  }, [open, token]);

  const loadSupervisors = async () => {
    try {
      setLoadingSupervisors(true);
      const data = await getPaginatedUsers({
        role: 'clinical_supervisor',
        sortBy: 'name',
        sortOrder: 'asc',
        limit: 100 // Get all supervisors
      }, token);
      setAvailableSupervisors(data.users || []);
    } catch (err) {
      console.error('Error loading supervisors:', err);
      // Don't show error for supervisor loading failure, just continue
    } finally {
      setLoadingSupervisors(false);
    }
  };

  useEffect(() => {
    if (!open || !therapist?._id) return;
    let cancelled = false;
    (async () => {
      setProfileLoading(true);
      try {
        const res = await api.get(`/admin/therapist-profile/${therapist._id}`, token);
        if (!cancelled) setProfile(res?.data || null);
      } catch (err) {
        if (!cancelled) setProfile(null);
        console.error('Error loading therapist profile:', err);
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, therapist, token]);

  const setProfileField = (key, value) => setProfile((p) => ({ ...(p || {}), [key]: value }));

  // Qualifications and Modalities are freeSolo Autocompletes: MUI only commits
  // a chip on Enter, so text typed and left in the box is silently dropped on
  // Save. Track the pending text and fold it in on blur AND on submit — the ref
  // makes Save correct even if the blur hasn't flushed to state yet.
  const [chipInputs, setChipInputs] = useState({ qualifications: '', modalities: '' });
  const chipInputsRef = useRef({ qualifications: '', modalities: '' });

  const setChipInput = (key, value) => {
    chipInputsRef.current = { ...chipInputsRef.current, [key]: value };
    setChipInputs((c) => ({ ...c, [key]: value }));
  };

  const withPendingChip = (key, list) => {
    const pending = (chipInputsRef.current[key] || '').trim();
    const current = Array.isArray(list) ? list : [];
    if (!pending || current.includes(pending)) return current;
    return [...current, pending];
  };

  const commitChipInput = (key) => {
    const pending = (chipInputsRef.current[key] || '').trim();
    if (!pending) return;
    setProfileField(key, withPendingChip(key, profile?.[key] || []));
    setChipInput(key, '');
  };

  useEffect(() => {
    if (open && therapist) {
      setMaxClientCapacity(therapist.therapistInfo?.maxClientCapacity || 20);
      setCanReceiveAutoAssignments(therapist.therapistInfo?.canReceiveAutoAssignments ?? true);

      // Set currently assigned supervisors
      const currentSupervisors = therapist.therapistInfo?.assignedSupervisors || [];
      setSelectedSupervisors(currentSupervisors);

      setError('');
      setChipInput('qualifications', '');
      setChipInput('modalities', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, therapist]);

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = ''; // allow re-selecting the same file after a failure

    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError('Invalid file type. Use JPEG, PNG, GIF or WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image too large. Maximum size is 5MB.');
      return;
    }

    try {
      setUploadingPhoto(true);
      setError('');
      const form = new FormData();
      form.append('photo', file);
      // Uploads immediately and persists server-side, so the URL is already
      // saved before Save Changes is pressed — mirrors the service-group logo.
      const res = await api.post(`/admin/therapist-profile/${therapist._id}/picture`, form, token);
      setProfileField('profilePictureUrl', res?.data?.profilePictureUrl || '');
    } catch (err) {
      console.error('Error uploading therapist photo:', err);
      setError(err.response?.data?.error || 'Photo upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Send only the two fields this dialog owns. The server merges these over
      // the stored therapistInfo, so echoing back the whole blob from the staff
      // list risks overwriting profile fields with stale values.
      await updateUserRoles(therapist._id, {
        roles: therapist.role,
        therapistInfo: { maxClientCapacity, canReceiveAutoAssignments }
      }, token);

      // Handle supervisor assignments if changed
      const currentSupervisorIds = (therapist.therapistInfo?.assignedSupervisors || [])
        .map(s => s._id || s);
      const newSupervisorIds = selectedSupervisors.map(s => s._id || s);

      // Find supervisors to add and remove
      const supervisorsToAdd = newSupervisorIds.filter(id => !currentSupervisorIds.includes(id));
      const supervisorsToRemove = currentSupervisorIds.filter(id => !newSupervisorIds.includes(id));

      // Remove supervisors first
      for (const supervisorId of supervisorsToRemove) {
        await removeTherapistFromSupervisor(supervisorId, therapist._id, token);
      }

      // Then add new supervisors
      if (supervisorsToAdd.length > 0) {
        await bulkAssignTherapistsToSupervisors([therapist._id], supervisorsToAdd, token);
      }

      // Client-facing profile fields, if the profile loaded
      if (profile) {
        await api.patch(`/admin/therapist-profile/${therapist._id}`, {
          displayName: profile.displayName || '',
          title: profile.title || '',
          pronouns: profile.pronouns || '',
          qualifications: withPendingChip('qualifications', profile.qualifications),
          modalities: withPendingChip('modalities', profile.modalities),
          shortBio: profile.shortBio || '',
          profilePictureUrl: profile.profilePictureUrl || '',
          acceptingNewClients: profile.acceptingNewClients !== false
        }, token);
      }

      onSuccess();
    } catch (err) {
      console.error('Error updating therapist:', err);
      // Prefer the server's message — profile validation failures (e.g. bio too
      // long) are returned as 400 with a readable explanation.
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to update therapist settings'
      );
      setLoading(false);
    }
  };

  if (!therapist) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon />
          Edit Therapist Settings
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ pt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {therapist.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
            {therapist.email}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Max Client Capacity"
              type="number"
              fullWidth
              value={maxClientCapacity}
              onChange={(e) => setMaxClientCapacity(parseInt(e.target.value) || 20)}
              inputProps={{ min: 1, max: 200 }}
              helperText={`Currently assigned: ${therapist.therapistInfo?.assignedClientCount || 0} clients`}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={canReceiveAutoAssignments}
                  onChange={(e) => setCanReceiveAutoAssignments(e.target.checked)}
                />
              }
              label="Enable automatic client assignments"
            />

            <Autocomplete
              multiple
              id="assigned-supervisors"
              options={availableSupervisors}
              getOptionLabel={(option) => option.name || ''}
              value={selectedSupervisors}
              onChange={(event, newValue) => setSelectedSupervisors(newValue)}
              loading={loadingSupervisors}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assigned Clinical Supervisors"
                  placeholder="Select supervisors..."
                  helperText="Therapists can have multiple supervisors"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSupervisors ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => option._id === value._id}
              disabled={loading}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom>
            Client-facing profile
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Shown to prospective clients on the invitation signup page and when choosing an
            appointment slot. Curated centrally so it can be reviewed before clients see it.
          </Typography>

          {profileLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!profileLoading && !profile && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Could not load this therapist's profile. Capacity and supervisor changes will still
              save; profile fields will not.
            </Alert>
          )}

          {!profileLoading && profile && (
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Display name"
                value={profile.displayName || ''}
                onChange={(e) => setProfileField('displayName', e.target.value)}
                helperText={`Shown as the therapist's name. Falls back to title + account name if blank${
                  profile.firstName || profile.lastName ? ` (currently "${[profile.title, profile.firstName, profile.lastName].filter(Boolean).join(' ')}")` : ''
                }.`}
                disabled={loading}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Title"
                  value={profile.title || ''}
                  onChange={(e) => setProfileField('title', e.target.value)}
                  placeholder="Dr, Ms, Mr…"
                  sx={{ width: 160 }}
                  disabled={loading}
                />
                <TextField
                  fullWidth
                  label="Pronouns"
                  value={profile.pronouns || ''}
                  onChange={(e) => setProfileField('pronouns', e.target.value)}
                  placeholder="she/her, he/him, they/them"
                  helperText="Shown beneath the name, and how clients tell therapists apart when choosing a slot"
                  disabled={loading}
                />
              </Box>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={profile.qualifications || []}
                onChange={(_, v) => { setProfileField('qualifications', v); setChipInput('qualifications', ''); }}
                inputValue={chipInputs.qualifications}
                onInputChange={(_, v) => setChipInput('qualifications', v)}
                onBlur={() => commitChipInput('qualifications')}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} size="small" />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Qualifications"
                    placeholder="Type and press Enter"
                    helperText="Press Enter after each. Displayed on the signup page, separated by dots"
                  />
                )}
                disabled={loading}
              />
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={profile.modalities || []}
                onChange={(_, v) => { setProfileField('modalities', v); setChipInput('modalities', ''); }}
                inputValue={chipInputs.modalities}
                onInputChange={(_, v) => setChipInput('modalities', v)}
                onBlur={() => commitChipInput('modalities')}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} size="small" />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Modalities"
                    placeholder="e.g. CBT, IFS, EMDR — press Enter after each"
                    helperText="Press Enter after each. Therapeutic approaches shown to prospective clients"
                  />
                )}
                disabled={loading}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Short bio"
                value={profile.shortBio || ''}
                onChange={(e) => setProfileField('shortBio', e.target.value.slice(0, 300))}
                error={(profile.shortBio || '').length >= 300}
                helperText={`Shown to prospective clients before they sign up — ${(profile.shortBio || '').length}/300 characters`}
                disabled={loading}
              />
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>Profile photo</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={profile.profilePictureUrl || undefined}
                    sx={{ width: 72, height: 72 }}
                  >
                    {(profile.displayName || profile.firstName || '?').charAt(0)}
                  </Avatar>
                  <Stack spacing={1}>
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      startIcon={uploadingPhoto ? <CircularProgress size={16} /> : <UploadIcon />}
                      disabled={loading || uploadingPhoto}
                    >
                      {uploadingPhoto ? 'Uploading…' : (profile.profilePictureUrl ? 'Replace photo' : 'Upload photo')}
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handlePhotoUpload}
                      />
                    </Button>
                    {profile.profilePictureUrl && (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setProfileField('profilePictureUrl', '')}
                        disabled={loading || uploadingPhoto}
                      >
                        Remove
                      </Button>
                    )}
                  </Stack>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  JPEG, PNG, GIF or WebP, up to 5MB. Uploads save immediately; Remove takes effect when
                  you press Save Changes.
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={profile.acceptingNewClients !== false}
                    onChange={(e) => setProfileField('acceptingNewClients', e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Accepting new clients"
              />
            </Stack>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditTherapistModal;
